import React, { useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { Youtube, ArrowRight, Loader2, Mail, Lock } from 'lucide-react';

export const AuthScreen: React.FC<{ intent?: 'free' | 'pro_monthly' | 'pro_annual', onAuthSuccess?: () => void }> = ({ intent, onAuthSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
        });
        if (error) throw error;
        setMessage("Conta criada! Verifique seu e-mail para confirmar (você pode continuar o app por enquanto na mesma sessão).");
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      }

      if (intent === 'pro_monthly' || intent === 'pro_annual') {
        setRedirecting(true);
        return; // App.tsx vai interceptar o usuário logado + intent e assumir o redirecionamento
      }

      if (onAuthSuccess) {
        onAuthSuccess();
      }

    } catch (err: any) {
      setError(err.message);
    } finally {
      if (!redirecting) {
        setLoading(false);
      }
    }
  };

  if (redirecting) {
    return (
      <div className="min-h-screen bg-[#F8F9FB] flex flex-col items-center justify-center p-4">
        <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
        <h2 className="text-2xl font-bold text-slate-900">Redirecionando para o pagamento seguro...</h2>
        <p className="text-slate-500 mt-2">Você será redirecionado para o Checkout da Stripe.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center p-4">
      <div className="bg-white rounded-[32px] border border-slate-100 shadow-xl shadow-slate-200/50 overflow-hidden w-full max-w-5xl flex flex-col md:flex-row min-h-[600px]">

        {/* Left Panel - Branding */}
        <div className="flex-1 bg-slate-900 p-12 text-white flex flex-col justify-between relative overflow-hidden">
          <div className="relative z-10">
            <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mb-8 border border-white/20">
              <Youtube size={24} />
            </div>
            <h1 className="text-4xl font-bold mb-4 leading-tight">
              Domine sua estratégia de conteúdo.
            </h1>
            <p className="text-slate-400 text-lg">
              Transforme horas de vídeo em minutos de leitura e planos de ação com IA.
            </p>
          </div>

          <div className="relative z-10 mt-12">
            <div className="flex -space-x-3 mb-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="w-10 h-10 rounded-full border-2 border-slate-900 bg-slate-800 flex items-center justify-center text-xs">
                  User
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-400">Junte-se a milhares de criadores.</p>
          </div>

          {/* Abstract Background Shapes */}
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-600 rounded-full blur-[100px] opacity-20 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black/50 to-transparent pointer-events-none"></div>
        </div>

        {/* Right Panel - Form */}
        <div className="flex-1 p-12 flex flex-col justify-center">
          <div className="max-w-sm mx-auto w-full">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {isSignUp ? 'Criar Conta' : 'Bem-vindo de volta'}
            </h2>
            <p className="text-slate-500 mb-8">
              {isSignUp ? 'Comece a gerar insights hoje.' : 'Entre para acessar seus projetos.'}
            </p>

            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg">
                {error}
              </div>
            )}

            {message && (
              <div className="mb-6 p-3 bg-green-50 border border-green-100 text-green-600 text-sm rounded-lg">
                {message}
              </div>
            )}

            <form onSubmit={handleAuth} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2 ml-1">E-mail</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2 ml-1">Senha</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white font-bold py-3.5 rounded-xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-lg shadow-slate-900/20 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    {isSignUp ? 'Cadastrar' : 'Entrar'} <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-slate-500 text-sm">
                {isSignUp ? 'Já tem uma conta?' : 'Não tem uma conta?'}
                <button
                  onClick={() => setIsSignUp(!isSignUp)}
                  className="ml-2 font-bold text-slate-900 hover:underline"
                >
                  {isSignUp ? 'Entrar' : 'Criar conta'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};